import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom'

// material ui stuff
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Drawer,
  useMediaQuery,
  useTheme,
  IconButton,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  TextField,
  List,
  Tooltip
} from '@mui/material'

// icons stuff
import MenuIcon from '@mui/icons-material/Menu'
import HomeIcon from '@mui/icons-material/Home'
import QueueMusicIcon from '@mui/icons-material/QueueMusic'
import SettingsIcon from '@mui/icons-material/Settings'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AddIcon from '@mui/icons-material/Add'
import PersonIcon from '@mui/icons-material/Person'
import LogoutIcon from '@mui/icons-material/Logout'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// redux stuff
import { useSelector, useDispatch } from 'react-redux'
import { logout } from '../actions/userActions'
import { getSongs, searchSongs } from '../actions/songActions'
import { getPublicPlaylists, deletePlaylist } from '../actions/playlistActions'

const Nav = () => {
  const [searchValue, setSearchValue] = useState('')
  const [tabValue, setTabValue] = useState(0)
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null)
  const [addMenuAnchor, setAddMenuAnchor] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const matches = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()

  const checkUrl = location.pathname.split('/')[1]
  const { userInfo } = useSelector(state => state.userState)
  const { songs, searchedSongs } = useSelector(state => state.songState)
  const { playlists, playlist } = useSelector(state => state.playlistState)

  useEffect(() => {
    setIsOpen(false)
  }, [location])

  useEffect(() => {
    if (matches && checkUrl !== 'profile') {
      dispatch(getSongs())
      dispatch(getPublicPlaylists())
    }
  }, [matches, checkUrl])

  const searchSongForm = e => {
    e.preventDefault()
    dispatch(searchSongs(searchValue))
  }

  const closeProfileMenu = () => setProfileMenuAnchor(null)
  const closeAddMenu = () => setAddMenuAnchor(null)

  const handleLogout = () => {
    dispatch(logout())
    navigate('/')
  }

  const changeTab = (event, newValue) => {
    setTabValue(newValue)
  }

  return (
    <AppBar sx={{ flexGrow: 1, backgroundColor: '#fff', py: matches ? 0.5 : 1 }} position="fixed" color='transparent' elevation={1}>
      <Toolbar sx={{ px: 0 }}>
        {matches && <>
          <IconButton onClick={() => setIsOpen(true)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <Drawer anchor='left' open={isOpen} onClose={() => setIsOpen(false)}>
            <Box sx={{ width: 275 }}>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
              {checkUrl !== 'playlists' ? <Box>
                {checkUrl === 'profile' ? <>
                  <Link to='/profile' style={{ textDecoration: 'none', color: 'inherit' }}>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ p: 2 }}>
                        <ListItemIcon>
                          <HomeIcon />
                        </ListItemIcon>
                        <ListItemText>Profile Home</ListItemText>
                      </ListItemButton>
                    </ListItem>
                  </Link>
                  <Divider />

                  <Link to='/profile/playlists' style={{ textDecoration: 'none', color: 'inherit' }}>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ p: 2 }}>
                        <ListItemIcon>
                          <QueueMusicIcon />
                        </ListItemIcon>
                        <ListItemText>Playlists</ListItemText>
                      </ListItemButton>
                    </ListItem>
                  </Link>
                  <Divider />

                  <Link to='/profile/settings' style={{ textDecoration: 'none', color: 'inherit' }}>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ p: 2 }}>
                        <ListItemIcon>
                          <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText>Settings</ListItemText>
                      </ListItemButton>
                    </ListItem>
                  </Link>
                  <Divider />
                </> : (
                  <>
                    <Tabs sx={{ mt: 2 }} value={tabValue} onChange={changeTab} centered>
                      <Tab sx={{ p: 0 }} label='Songs' />
                      <Tab sx={{ p: 0 }} label='Playlists' />
                      <Tab sx={{ p: 0 }} label='Search' />
                    </Tabs>
                    {tabValue === 0 && <List>
                      <Divider sx={{ mt: 3 }} />
                      {songs && songs.map((song, index) => (
                        <Link style={{ textDecoration: 'none', color: 'inherit' }} to={`/song/${song._id}`} key={song._id}>
                          <ListItem disablePadding>
                            <ListItemButton sx={{ p: 2 }}>
                              <ListItemText>{index + 1}. {song.title}</ListItemText>
                            </ListItemButton>
                          </ListItem>
                          <Divider />
                        </Link>
                      ))}
                    </List>}

                    {tabValue === 1 && <List>
                      <Divider />
                      {playlists && playlists.map(playlist => (
                        <Link style={{ textDecoration: 'none', color: 'inherit' }} to={`/playlists/${playlist._id}`} key={playlist._id}>
                          <ListItem disablePadding>
                            <ListItemButton sx={{ p: 2 }}>
                              <ListItemText secondary={playlist.songs.length + ` ${playlist.songs.length === 1 ? 'song' : 'songs'}`} primary={playlist.title} />
                            </ListItemButton>
                          </ListItem>
                          <Divider />
                        </Link>
                      ))}
                    </List>}

                    {tabValue === 2 && <List>
                      <form style={{ paddingLeft: '10px', paddingRight: '10px' }} onSubmit={searchSongForm}>
                        <TextField
                          label='Search'
                          sx={{ my: 2 }}
                          fullWidth
                          value={searchValue}
                          onChange={e => setSearchValue(e.target.value)}
                        />
                        <Button fullWidth variant='contained' type='submit' disabled={searchValue.length === 0}>Search</Button>
                        <Divider sx={{ mt: 2 }} />
                        {searchedSongs && searchedSongs.map(song => (
                          <Link style={{ textDecoration: 'none', color: 'inherit' }} to={`/song/${song._id}`} key={song._id}>
                            <ListItem disablePadding>
                              <ListItemButton sx={{ p: 2 }}>
                                <ListItemText>{song.title}</ListItemText>
                              </ListItemButton>
                            </ListItem>
                            <Divider />
                          </Link>
                        ))}
                      </form>
                    </List>}
                  </>
                )}
              </Box> : <Box>
                {playlist && <Box>
                  <Box sx={{ mt: 4, mb: 2, px: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>

                    <Link style={{ textDecoration: 'none', color: 'inherit', width: '100%' }} to='/'>
                      <ListItem disablePadding>
                        <ListItemButton sx={{ px: 2, py: 3 }}>
                          <ListItemIcon>
                            <ArrowBackIcon />
                          </ListItemIcon>
                          <Typography variant='h6' sx={{ fontWeight: 400, fontSize: 20 }}>Go back</Typography>
                        </ListItemButton>
                      </ListItem>
                      <Divider />
                    </Link>
                    <ListItemText secondary={`Created by ${playlist.user.name}`}>
                      <Typography variant='h6'>{playlist.title} asdf</Typography>
                    </ListItemText>
                    {userInfo && (userInfo.role === 'admin' || userInfo.role === 'superadmin' || userInfo._id === playlist.user._id) && <Box>
                      <Link style={{ textDecoration: 'none', color: 'inherit' }} to={`/edit/playlists/${playlist._id}`}>
                        <Tooltip title='Edit'>
                          <IconButton>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Link>
                      <Tooltip title='Delete'>
                        <IconButton onClick={() => dispatch(deletePlaylist(playlist._id, navigate))}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>}
                  </Box>
                  <Divider />
                  {playlist.songs.map(song => (
                    <Link style={{ textDecoration: 'none', color: 'inherit' }} to={`/playlists/${playlist._id}/${song.song._id}`} key={song.song._id}>
                      <ListItem disablePadding>
                        <ListItemButton sx={{ px: 2, py: 3 }}>
                          <Typography variant='h6' sx={{ fontWeight: 400, fontSize: 20 }}>{song.song.title}</Typography>
                        </ListItemButton>
                      </ListItem>
                      <Divider />
                    </Link>
                  ))}
                </Box>}
              </Box>}
            </Box>
          </Drawer>
        </>}

        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          <Link to='/' style={{ width: '100%', color: 'inherit', textDecoration: 'none' }}>Eclesia Songs</Link>
        </Typography>

        {!userInfo ? <Link to='/login' style={{ color: 'inherit', textDecoration: 'none' }}>
          <Button sx={{ mr: 2 }} variant='contained'>Login</Button>
        </Link> : (
          <>
            <IconButton onClick={e => setAddMenuAnchor(e.currentTarget)}>
              <AddIcon fontSize='large' />
            </IconButton>
            <Menu elevation={3} anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={closeAddMenu}>
              {userInfo.role === 'admin' || userInfo.role === 'superadmin' ?
                <Link to='/add-song' style={{ textDecoration: 'none' }}>
                  <MenuItem sx={{ p: 2, px: 3 }} onClick={closeAddMenu}>
                    <ListItemIcon>
                      <MusicNoteIcon />
                    </ListItemIcon>
                    Add Song
                  </MenuItem>
                  <Divider />
                </Link> : null}
              <Link to='/add-playlist' style={{ textDecoration: 'none' }}>
                <MenuItem sx={{ p: 2, px: 3 }} onClick={closeAddMenu}>
                  <ListItemIcon>
                    <QueueMusicIcon />
                  </ListItemIcon>
                  Add Playlist
                </MenuItem>
              </Link>
            </Menu>

            <IconButton sx={{ ml: 1 }} onClick={e => setProfileMenuAnchor(e.currentTarget)}>
              <AccountCircleIcon fontSize='large' />
            </IconButton>
            <Menu elevation={3} anchorEl={profileMenuAnchor} open={Boolean(profileMenuAnchor)} onClose={closeProfileMenu}>
              <Link to='/profile' style={{ textDecoration: 'none' }}>
                <MenuItem sx={{ p: 2, px: 3 }} onClick={closeProfileMenu}>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  Profile
                </MenuItem>
              </Link>
              <Divider />
              <MenuItem sx={{ p: 2, px: 3 }} onClick={() => {
                closeProfileMenu()
                handleLogout()
              }}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>


    </AppBar>
  )
}

export default Nav